// LocalStorage Analytics Provider
//
// This provider handles persistence of analytics context to localStorage.
// It doesn't track individual events, but instead persists context changes
// (sessionId, lastActiveSessionId, clientId) for cross-session continuity.

import { roseStorage } from '../../services/rose-storage';
import { logger } from '../../utils/core/logger';
import { maskId } from '../../utils/core/mask';
import type { AnalyticsContext, AnalyticsEvent } from '../types';
import { BaseAnalyticsProvider } from './base';

export class LocalStorageProvider extends BaseAnalyticsProvider {
    public readonly name = 'localstorage';

    /**
     * LocalStorage provider doesn't track individual events,
     * only context updates via updateContext()
     */
    public eventFilter(_event: AnalyticsEvent): boolean {
        return false;
    }

    /**
     * Initialize provider with initial context
     * This persists the initial values (apiVersion, environment, etc.) to localStorage
     */
    public override async initialize(context: AnalyticsContext): Promise<void> {
        // Call base implementation to store context
        await super.initialize(context);

        // Persist initial context values to localStorage
        logger.debug('📦 LocalStorage: initialize called with context:', context);
        this.persistContext(context);
    }

    /**
     * Persist analytics context to localStorage
     * This is called whenever the global analytics context changes
     */
    public override updateContext(context: Partial<AnalyticsContext>): void {
        logger.debug('📦 LocalStorage: updateContext called with:', context);

        // Call base implementation to update internal context
        super.updateContext(context);

        // Persist context to localStorage
        this.persistContext(context);
    }

    /**
     * Internal method to persist context values to localStorage
     * Shared by both initialize() and updateContext()
     */
    private persistContext(context: Partial<AnalyticsContext>): void {
        // Persist session tracking to localStorage
        if (context.sessionId !== undefined) {
            roseStorage.setLastSessionId(context.sessionId);
            logger.debug(`📦 LocalStorage: ✅ Stored sessionId: ${maskId(context.sessionId)}`);
        }

        if (context.lastActiveSessionId !== undefined) {
            roseStorage.setLastActiveSessionId(context.lastActiveSessionId);
            logger.debug(
                `📦 LocalStorage: ✅ Stored lastActiveSessionId: ${maskId(context.lastActiveSessionId)}`
            );
        }

        if (context.lastActiveSessionDate !== undefined) {
            roseStorage.setLastActiveSessionDate(context.lastActiveSessionDate);
            logger.debug(
                `📦 LocalStorage: ✅ Stored lastActiveSessionDate: ${context.lastActiveSessionDate ? new Date(context.lastActiveSessionDate).toISOString() : 'null'}`
            );
        }

        if (context.clientId !== undefined) {
            roseStorage.setAnalyticsClientId(context.clientId);
            logger.debug(`📦 LocalStorage: Stored clientId: ${maskId(context.clientId)}`);
        }

        if (context.apiVersion !== undefined) {
            roseStorage.setApiVersion(context.apiVersion);
            logger.debug(`📦 LocalStorage: Stored apiVersion: ${context.apiVersion}`);
        }

        if (context.frontendVersion !== undefined) {
            roseStorage.setFrontendVersion(context.frontendVersion);
            logger.debug(`📦 LocalStorage: Stored frontendVersion: ${context.frontendVersion}`);
        }
    }

    /**
     * LocalStorage doesn't track individual events
     */
    protected async sendEvent(_event: AnalyticsEvent): Promise<void> {
        // No-op: LocalStorage only persists context, not individual events
    }
}
